package ai.jessie.web.documents

import ai.jessie.shared.documents.DocumentResponse
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardFootStyle
import ai.jessie.web.theme.CardHeadStyle
import ai.jessie.web.theme.CardStackStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.DefinitionsStyle
import ai.jessie.web.theme.DocumentItemMetaStyle
import ai.jessie.web.theme.DocumentItemStyle
import ai.jessie.web.theme.DocumentItemTextStyle
import ai.jessie.web.theme.DocumentItemTitleStyle
import ai.jessie.web.theme.DocumentLayoutStyle
import ai.jessie.web.theme.DocumentListStyle
import ai.jessie.web.theme.DocumentThumbStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SideItem
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.columns
import ai.jessie.web.theme.sideNav
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// UC-017 steps 1-9 and UC-020 B8 in the design-v2 frame (`07-Dokumente.png`): one side entry per
// workflow state, the upload card above the Inbox list and the opened document beside the list.
// `documentId` is null on a list route. Visibility is presentation of what the server returned; a
// hidden duplicate (UC-017 A8) never appears. [lost] receives a session or organisation loss found
// while processing.
@Composable
fun documentInbox(
    organisationId: String,
    documentId: String?,
    filter: DocumentFilter,
    state: DocumentUiState,
    actions: DocumentActions,
    lost: (OnboardingProblem) -> Unit,
) {
    state.error?.let { Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(it, state.loading) } }
    if (state.error?.authenticationRequired != true) {
        if (state.loading)
            P(attrs = StatusStyle.toAttrs { attr("role", "status") }) {
                Text("Dokumente werden geladen …")
            }
        if (state.error == null || state.documents != null) {
            columns(
                side = {
                    sideNav(
                        DocumentFilter.entries.map {
                            SideItem(it.label, documentsHref(organisationId, it), it.icon)
                        },
                        documentsHref(organisationId, filter),
                        DOCUMENTS_TITLE,
                    )
                }
            ) {
                Div(attrs = DocumentLayoutStyle.toAttrs()) {
                    Div(attrs = CardStackStyle.toAttrs()) {
                        // Uploads always arrive in the Inbox (UC-017).
                        if (filter == DocumentFilter.INBOX)
                            documentUpload(organisationId, state, actions)
                        documentList(organisationId, documentId, filter, state, actions)
                    }
                    when {
                        documentId == null ->
                            P(attrs = HintStyle.toAttrs()) {
                                Text("Wähle ein Dokument aus der Liste, um es zu bearbeiten.")
                            }
                        // A4: without a confirmed list no opened document stays either.
                        state.documents != null ->
                            documentProcessing(
                                organisationId,
                                documentId,
                                filter,
                                actions.refresh,
                                lost,
                            )
                    }
                }
            }
        } else
            Div(attrs = RowStyle.toAttrs()) {
                themedButton("Dokumente erneut laden", actions.refresh, disabled = state.loading)
            }
    }
    P { A(href = "/organisations/$organisationId") { Text("Zurück zur Organisation") } }
}

// UC-017 steps 1 and 9, B6, UC-020 B8: the documents of one state the server returned, in its
// order.
@Composable
private fun documentList(
    organisationId: String,
    documentId: String?,
    filter: DocumentFilter,
    state: DocumentUiState,
    actions: DocumentActions,
) {
    val documents = state.documents ?: return
    val inbox = filter == DocumentFilter.INBOX
    Div(attrs = CardStyle.toAttrs()) {
        Div(attrs = CardHeadStyle.toAttrs()) {
            H2(attrs = { id(INBOX_TITLE_ID) }) { Text("${filter.label} (${documents.size})") }
        }
        if (documents.isEmpty())
            Div(attrs = CardBodyStyle.toAttrs()) {
                P(attrs = HintStyle.toAttrs()) {
                    Text(
                        if (inbox) "Noch keine Dokumente." else "Keine Dokumente in diesem Status."
                    )
                }
            }
        else
            Ul(attrs = DocumentListStyle.toAttrs { attr("aria-labelledby", INBOX_TITLE_ID) }) {
                documents.forEach { item ->
                    Li {
                        listItem(
                            organisationId,
                            item,
                            filter,
                            item.documentId.equals(documentId, true),
                        )
                    }
                }
            }
        Div(attrs = CardFootStyle.toAttrs()) {
            themedButton(
                if (inbox) "Inbox erneut laden" else "Liste erneut laden",
                actions.refresh,
                disabled = state.loading || state.phase == UploadPhase.UPLOADING,
            )
        }
    }
}

@Composable
private fun listItem(
    organisationId: String,
    item: DocumentResponse,
    filter: DocumentFilter,
    current: Boolean,
) {
    A(
        href = documentHref(organisationId, item.documentId, filter),
        attrs = DocumentItemStyle.toAttrs { if (current) attr("aria-current", "page") },
    ) {
        Span(attrs = DocumentThumbStyle.toAttrs { attr("aria-hidden", "true") }) {
            Text(documentTypeLabel(item.mediaType))
        }
        Span(attrs = DocumentItemTextStyle.toAttrs()) {
            Span(attrs = DocumentItemTitleStyle.toAttrs()) { Text(item.fileName) }
            Span(attrs = DocumentItemMetaStyle.toAttrs()) {
                Text(
                    "${documentTypeLabel(item.mediaType)} · ${sizeLabel(item.sizeBytes)} · " +
                        uploadTimeLabel(item.uploadedAt)
                )
            }
        }
    }
}

// The validated (step 5) or stored (B6) facts; only a stored document has an upload time.
@Composable
internal fun facts(
    fileName: String,
    mediaType: String,
    sizeBytes: Long,
    uploadedAt: String? = null,
) {
    TagElement<HTMLElement>("dl", DefinitionsStyle.toAttrs()) {
        TagElement<HTMLElement>("dt", null) { Text("Dateiname") }
        TagElement<HTMLElement>("dd", null) { Text(fileName) }
        TagElement<HTMLElement>("dt", null) { Text("Typ") }
        TagElement<HTMLElement>("dd", null) { Text(documentTypeLabel(mediaType)) }
        TagElement<HTMLElement>("dt", null) { Text("Grösse") }
        TagElement<HTMLElement>("dd", null) { Text(sizeLabel(sizeBytes)) }
        uploadedAt?.let {
            TagElement<HTMLElement>("dt", null) { Text("Hochgeladen") }
            TagElement<HTMLElement>("dd", null) { Text(uploadTimeLabel(it)) }
        }
    }
}

// The Inbox keeps the UC-017 routes; another state is the `status` query (UC-020 B8).
internal fun documentsHref(
    organisationId: String,
    filter: DocumentFilter = DocumentFilter.INBOX,
) = "/organisations/$organisationId/documents${statusQuery(filter)}"

internal fun documentHref(
    organisationId: String,
    documentId: String,
    filter: DocumentFilter = DocumentFilter.INBOX,
) = "/organisations/$organisationId/documents/$documentId${statusQuery(filter)}"

private fun statusQuery(filter: DocumentFilter) =
    if (filter == DocumentFilter.INBOX) "" else "?status=${filter.name}"

internal const val DOCUMENTS_TITLE = "Dokumente"
internal const val FILE_INPUT_ID = "document-file"
private const val INBOX_TITLE_ID = "document-inbox-title"
